/** Every template field kind, in the glossary's order. */
export const AGENT_TEMPLATE_FIELD_KIND = ["upload", "api", "book", "text", "choice", "website"];
/**
 * The first required group of `fieldSet` none of whose fields `isGiven` (in field-set order), or `null` when every group
 * has at least one. A field hidden by its `when` is the caller's to judge: `isGiven` answers for it. The api answers
 * `400 information_input_missing` with `detail.group` and `detail.field_code[]`.
 */
export function missingRequiredGroupOf(fieldSet, isGiven) {
    const byGroup = new Map();
    for (const field of fieldSet) {
        if (field.requiredGroup === undefined)
            continue;
        const member = byGroup.get(field.requiredGroup);
        if (member === undefined)
            byGroup.set(field.requiredGroup, [field]);
        else
            member.push(field);
    }
    for (const [group, member] of byGroup) {
        if (!member.some(isGiven))
            return Object.freeze({ group, fieldCode: Object.freeze(member.map((one) => one.code)) });
    }
    return null;
}
/**
 * What a conversation engine offers beyond answering (plan-018 DOM, design §30.0.3 D6): the information retrieval step,
 * the `take_message` tool, the `hand_off_to_person` tool. Catalogue data, so adding a template never changes runtime
 * code (F-FAC-1); the runtime derives the fixed tool order, the HANDOFF line and the retrieval step from it.
 *
 * AMENDED 2026-10-15 (plan-019 TPL, design §31.3.4, §31.6.3, ADR 0065; glossary 2026-10-15): the hidden `help`
 * template's four capabilities are appended, in design §31.3.4's order — `help_request_taking` (`offer_help_request`),
 * `help_proposal` (`propose_navigation`, `propose_form_values`), `standard_talk_rate` (`read_talk_rates`) and
 * `operator_takeover` (`ask_for_person`). Appended, so every declaration before plan-019 keeps its order; no customer
 * template declares them. `standard_talk_rate` is a capability word here, never the rate table: other modules name it
 * through {@link STANDARD_TALK_RATE_CAPABILITY} (the `talk-rate-reader` rule lists this file for the literal alone).
 */
export const ENGINE_CAPABILITY = [
    "information_retrieval",
    "message_taking",
    "person_handoff",
    "help_request_taking",
    "help_proposal",
    "standard_talk_rate",
    "operator_takeover",
];
/** The capability whose tool reads the standard talk rates (`read_talk_rates`, design §31.6.3) — the help template's. */
export const STANDARD_TALK_RATE_CAPABILITY = "standard_talk_rate";
/** The hidden `help` template's capabilities (design §31.3.4, IC-H15), in {@link ENGINE_CAPABILITY}'s order. */
export const HELP_ENGINE_CAPABILITY = [
    "information_retrieval",
    "help_request_taking",
    "help_proposal",
    STANDARD_TALK_RATE_CAPABILITY,
    "operator_takeover",
];
/** The declaration's capabilities — none when it declares none. */
export function engineCapabilityOf(declaration) {
    return declaration.capability === undefined ? [] : declaration.capability;
}
/**
 * Whether an engine answers at every hour (owner decision 2026-10-14, ADR 0061 amendment; design §30.19): an engine that
 * answers from the owner's information (`information_retrieval`) is admitted whatever the opening hours and special
 * dates say — they decide only when a caller can be put through to a person. Every other engine (delivery, survey)
 * keeps the after-hours plan at admission. Decided by the capability, never by the template code (D6).
 */
export function answersOutsideOpeningHours(capability) {
    return capability.includes("information_retrieval");
}
/**
 * Who a template's VoiceAgents talk to (plan-021 DOM, ED-2, design §33.3.1, ADR 0073 proposed; glossary §6.37 *VoiceAgent
 * audience*): `external` — the business's callers and visitors; `internal` — the business's own team (the BI Agent,
 * which reads connected data). Catalogue data, never an {@link EngineCapability}: a capability would give the engine a
 * `ConversationSupport` and change its prompts byte for byte (investigation finding 1). The portal's internal tag and
 * the pass's audience wall read this one value. *(Per OD-3 — recommended; accepted by the owner at AP-0, 2026-10-19.)*
 */
export const VOICE_AGENT_AUDIENCE = ["external", "internal"];
//# sourceMappingURL=catalogue.js.map